import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { sharedGet } from './sharedGet';
import { AlertTriangle, Database, Loader2 } from 'lucide-react';
import { BoxOverlay } from './BoxOverlay';
import { ImageLightbox } from './ImageLightbox';
import {
  setGalleryView,
  setSubmodelHeroBox, useSubmodel,
} from './listingStore';
import { displayVariantLabel, variantLabel } from './variantLabel';
import { DIRECTION_IDS, OTHER_DIRECTION, directionLabel, directionRank } from './viewDirections';
import { DimensionBadge } from './DimensionBadge';
import { useLanguage } from '../../contexts/LanguageContext';
import { PICKED } from './pickedLook';
import { useStoredPhotosEpoch } from './listingPhotosSave';

// The per-picture "in the database" switch, mirroring Phase 2's Add-to-database toggle:
// the stored (validated) set is what a later run reuses, so this is where a picture is
// kept or dropped from it. `inDb` is membership; `onToggle` adds or removes and resolves
// once the server has answered (the parent then refetches).
function ListingDbToggle({ inDb, onToggle }: { inDb: boolean; onToggle: () => Promise<void> }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onToggle();
    } catch {
      /* leave the toggle as it was; the parent's refetch is the source of truth */
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={click}
      disabled={busy}
      role="switch"
      aria-checked={inDb}
      title={t(inDb ? 'notice.step3.addedToDb' : 'notice.step3.addToDb')}
      aria-label={t(inDb ? 'notice.step3.addedToDb' : 'notice.step3.addToDb')}
      // AN ICON IN THE PICTURE'S CORNER, opposite the zoom and drawn like it: the pill under
      // the thumbnail said "Added to data…" in a width that could not hold it. Filled blue
      // when the picture is in, the zoom's white when it is not; the words are the tooltip.
      className={`absolute left-1 top-1 rounded-md border p-0.5 shadow-sm backdrop-blur transition-colors disabled:opacity-50 ${
        inDb
          ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-500'
          : 'border-slate-300 bg-white/90 text-slate-500 hover:border-blue-500 hover:text-blue-600'
      }`}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
    </button>
  );
}

// The listing search in progress: the spinner, plus how long it has been running. It
// mounts only while `detecting` is true and unmounts when the read ends, so the elapsed
// count starts fresh at each search with no reset to remember.
function SearchingSpinner() {
  const { t } = useLanguage();
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - start) / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="flex items-center justify-center gap-2 py-2 text-slate-400">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-xs tabular-nums">
        {t('notice.listing.researching')} {seconds}s
      </span>
    </div>
  );
}

// Every picture the listing shows, and which of them becomes the splash.
//
// og:image leads the strip — the merchant's own pick for the page — and the pictures
// with no product in them (a fabric swatch, a colour chip) were dropped by the filter
// call, which the badge answers for.
//
// The splash is CHOSEN out of this strip rather than assumed. og:image is picked to
// sell a click, so it is reliably a room with several units in it, and the pictures
// that would make a splash are the ones no tag points at. So a model picks the shot
// the product is cleanest in and boxes it there — and the user overrules it, by
// clicking another thumbnail or dragging the corners. Both are the last word; nothing
// re-asks, because nothing here is the model's to defend.
export function ListingGallery({ docId, submodelId }: { docId: string; submodelId: string }) {
  const { t } = useLanguage();
  const listing = useSubmodel(docId, submodelId);
  const {
    gallery, gallerySource, galleryViews, galleryDimensioned,
    heroUrl, heroBox,
    detecting, pending,
  } = listing;
  const [zoom, setZoom] = useState<string | null>(null);
  // Which gallery URLs are stored in this notice's DB set (url -> row id), so each
  // thumbnail can show and flip its membership. Refetched on open, after a search lands
  // (detecting falls), and after every toggle (dbVersion) — the server is authoritative.
  const [dbUrlIds, setDbUrlIds] = useState<Map<string, number>>(new Map());
  const [dbVersion, setDbVersion] = useState(0);
  // And when photographs were saved without being asked (see listingPhotosSave).
  const savedEpoch = useStoredPhotosEpoch(docId);
  useEffect(() => {
    let cancelled = false;
    void sharedGet<{ images: { id: number; url: string }[] }>(
        `/api/notice/listing-images/${encodeURIComponent(docId)}`,
      )
      .then(({ data }) => {
        if (cancelled) return;
        setDbUrlIds(
          new Map(
            (data.images || [])
              .filter((i) => i.url)
              .map((i) => [i.url, i.id] as const),
          ),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [docId, dbVersion, detecting, savedEpoch]);
  // Add this picture to the notice's DB set, or remove it — the switch under each
  // thumbnail. A removed picture is no longer part of what a later run reuses.
  const toggleDb = async (src: string) => {
    const id = dbUrlIds.get(src);
    if (id != null) await axios.delete(`/api/notice/listing-image/${id}`);
    // A hand-added picture carries this submodel's ID, so it groups with the rest of its
    // colour in the Product photos table and the public picker — and keeps grouping with
    // them when a re-read rewords the materials. The label rides along for display only,
    // and re-stamps the group's caption on every save.
    else await axios.post('/api/notice/listing-images', {
      docId,
      urls: [src],
      variant: submodelId,
      variantLabel: displayVariantLabel(variantLabel(listing)),
      // What is known about this picture goes in WITH it. A dimension drawing that
      // arrives in the database as an unremarkable photograph has lost the one fact
      // that made it worth keeping.
      dimensioned: galleryDimensioned[gallery.indexOf(src)] ? [src] : [],
    });
    setDbVersion((v) => v + 1);
  };
  // Merchant URLs the browser refused to load. The thumbnails render the merchant's CDN
  // links directly, so a merchant that blocks hotlinking fails HERE (never server-side,
  // where the bytes were fetched fine). We don't know this has ever happened — this makes
  // it visible if it does: a red tile per picture and a banner counting them.
  const [broken, setBroken] = useState<Set<string>>(new Set());
  const markBroken = (src: string) =>
    setBroken((prev) => (prev.has(src) ? prev : new Set(prev).add(src)));
  // Only the ones actually in the strip on screen — a re-search swaps the URLs, and a
  // stale entry must not inflate the count.
  const brokenShown = gallery.filter((u) => broken.has(u)).length;

  // Shown by direction rather than in the merchant's order: front, back, the sides, the
  // three-quarters, top and bottom, then everything that is no view at all. The strip
  // stops being a shop window and becomes the thing it is actually read as — what we have
  // of this product and what is missing, answered by looking at where it stops.
  //
  // A VIEW of the array, not a rewrite of it. `i` is each picture's place in the stored
  // gallery, which is what galleryViews is indexed by and what a correction writes back
  // to; reordering the stored array would mean reordering that one in step forever, and
  // the merchant's own order is a fact worth not destroying. The sort is stable, so ties
  // — and a strip nobody has classified — keep it.
  const ordered = useMemo(
    () => gallery
      .map((src, i) => ({ src, i, view: galleryViews[i] }))
      .sort((a, b) => directionRank(a.view) - directionRank(b.view)),
    [gallery, galleryViews],
  );

  // A read going now, OR queued for this run (reads are serial): a spinner instead of the
  // previous run's strip, so a fresh run doesn't leave stale found-images on screen while
  // this link waits its turn.
  if (detecting || pending) return <SearchingSpinner />;

  // Empty gallery: say WHY, rather than leaving a blank under the IMAGES rule — a read
  // that failed (red) or a listing that had no usable image. No link at all says nothing:
  // the empty link field beside it already does.
  if (!gallery.length) {
    if (!listing.error && !listing.url) return null;
    const message = listing.error || t('notice.listing.noImages');
    return (
      <div className="flex flex-col items-center gap-2">
        <p
          className={`text-center text-sm ${
            listing.error ? 'text-rose-600' : 'text-slate-500'
          }`}
        >
          {message}
        </p>
      </div>
    );
  }

  return (
    // FROM THE LEFT, not centred: the strip is as wide as its pictures and the table scrolls
    // over it, so a centred heading sat in the middle of a row mostly off screen.
    <div className="flex flex-col items-start gap-3">
      {/* No cover preview here: the splash is the picked thumbnail (outlined in the strip
          below), and its crop is edited by zooming it — see the lightbox at the bottom. */}
      {/* No heading of its own: it is a cell, and its column says what it is. */}

      {/* Only if the browser actually refused a picture — a precise, countable signal
          that the merchant is blocking hotlinking, so this failure mode can be told
          apart from a listing that simply had no images. */}
      {brokenShown > 0 && (
        <p className="flex items-center gap-1.5 text-center text-xs text-rose-600">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            {brokenShown} {t('notice.listing.imgBlockedBanner')}
          </span>
        </p>
      )}

      {/* One row, always, sliding: wrapped, the strip broke into a block of pictures
          with one lonely tile on a second line, and a picture's place in the merchant's
          gallery stopped being visible at all.

          It does not scroll ITSELF: it is a cell of the Listings table, and the table
          slides as a whole (see ListingsTable), so a row's link, materials and pictures
          stay one row while you drag. */}
        <div className="flex w-max gap-2 p-1">
        {ordered.map(({ src, i }) => (
          <div key={src} className="flex shrink-0 flex-col items-center gap-1.5">
          <div
            className={`relative rounded-lg border-2 p-1 transition-colors ${
              src === heroUrl
                ? PICKED
                : 'border-slate-300 bg-white hover:border-blue-400'
            }`}
          >
            {/* THE PICTURE OPENS IT, and picks nothing: the splash is the model's choice, and
                it is kept as the model made it. */}
            <button
              type="button"
              onClick={() => setZoom(src)}
              title={t('notice.pdf.zoom')}
              className="flex h-24 w-24 cursor-zoom-in items-center justify-center"
            >
              {/* The merchant's aspect ratios vary (a square studio shot, a wide
                  drawing), so the cell is fixed and the picture contained in it:
                  cropping a thumbnail to a grid would hide the very thing a drawing is
                  here to show.

                  Contained by `max-h`/`max-w` on the picture rather than by
                  `object-contain` on a fixed square. Both letterbox it identically —
                  but object-contain letterboxes it INSIDE the element, so the element
                  stops being the picture, and the crop below would be drawn against the
                  letterbox. Here the span shrink-wraps the photo, and the flex cell
                  centres it. */}
              <span className="relative inline-block">
                {broken.has(src) ? (
                  // The browser refused this URL: a red tile in its place, naming the
                  // likely cause, so a hotlink block is visible rather than a silent gap.
                  <span
                    title={t('notice.listing.imgBlocked')}
                    className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded border border-dashed border-rose-300 text-rose-500"
                  >
                    <AlertTriangle className="h-6 w-6" />
                    <span className="text-[10px] font-semibold uppercase tracking-wide">
                      {t('notice.listing.imgFailed')}
                    </span>
                  </span>
                ) : (
                  <img
                    src={src}
                    alt=""
                    loading="lazy"
                    onError={() => markBroken(src)}
                    className="block max-h-24 max-w-24 rounded"
                  />
                )}
                {/* The same crop as the splash above, at thumbnail size: this is the
                    picture it was drawn on, so it is the one place the strip can say
                    what the answer up there actually is. */}
                {src === heroUrl && heroBox && <BoxOverlay box={heroBox} rounded="rounded" />}
              </span>
            </button>
            {/* Same switch as Phase 2's Add-to-database, per listing picture: keep it in
                (or drop it from) the set a later run reuses. */}
            <ListingDbToggle inDb={dbUrlIds.has(src)} onToggle={() => toggleDb(src)} />
            {/* Measurements on it, as the model read them: bottom-left, only when yes. */}
            {galleryDimensioned[i] === true && <DimensionBadge />}
            </div>
            {/* Which side of the product this picture shows — the answer the 3D pipeline
                reads, so it is shown where the answer is, on the picture itself.

                A named direction is emerald, because those are the pictures worth
                something: one of them can be dropped straight into a reconstructor's
                view slot. "Other" stays grey and quiet — it IS the answer for most of a
                retail strip (a room scene, a dimension drawing, two chairs at a table),
                and eight loud badges saying so would bury the one that says "back".

                Nothing at all when the strip has no directions: unclassified and
                classified-as-nothing are different facts, and a grey "Other" under every
                thumbnail would claim the second while meaning the first. */}
            {galleryViews[i] && (
              // A native <select>, not a popup of our own. The strip is a drag-scrolled
              // overflow container, and any menu we drew inside it would be clipped by
              // the very element that lets the strip slide; the browser's own list is
              // drawn outside the page and cannot be. It also arrives with the keyboard
              // and the touch behaviour already done.
              //
              // Styled as the label it replaces — no arrow, sized to the text — so the
              // strip still reads as captions rather than as a row of form controls. The
              // title says it can be changed, since nothing else does.
              <select
                value={galleryViews[i]}
                onChange={(e) => setGalleryView(docId, submodelId, i, e.target.value)}
                title={t('notice.listing.setView')}
                aria-label={t('notice.listing.setView')}
                // Opaque white for every state, including OTHER: a transparent "quiet"
                // look read fine on the old white page but disappeared on the blue glow.
                // OTHER stays visually quieter through colour (slate, not blue) rather
                // than through a background that stops existing.
                className={`max-w-24 cursor-pointer appearance-none truncate rounded-full bg-white px-2 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide transition-colors hover:ring-1 hover:ring-blue-400 ${
                  galleryViews[i] === OTHER_DIRECTION
                    ? 'text-slate-500'
                    : 'text-blue-700'
                }`}
              >
                {DIRECTION_IDS.map((d) => (
                  // Explicit colours: an <option> is drawn by the OS, which does not
                  // inherit the pill's, and on a dark theme the list came out unreadable.
                  <option key={d} value={d} className="bg-white text-slate-900">
                    {directionLabel(d)}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
        </div>

      {/* ONLY WHEN THE PICTURES ARE NOT THE READ'S: the listing search is what a strip
          normally is, and saying so under every one was a line nobody needed. The kept
          (validated) DB set standing in for an empty read is the exception worth naming. */}
      {gallerySource === 'db' && (
        <p className="text-center text-xs italic text-slate-500">{t('notice.listing.fromDb')}</p>
      )}


      {/* The zoom steps through the whole strip: these pictures only mean anything
          against each other — which shows the product alone, which is a detail — and
          comparing them one reopen at a time is not comparing. */}
      <ImageLightbox
        src={zoom}
        // The order on screen, so stepping through the zoom walks the strip the eye just
        // walked rather than the merchant's order behind it.
        srcs={ordered.map((o) => o.src)}
        onNavigate={setZoom}
        alt={t('notice.listing.gallery')}
        // Only on the splash's own picture: step to the next photo and the box is gone,
        // because a box is coordinates inside ONE image and would frame whatever
        // happens to lie at them in another.
        box={zoom === heroUrl ? heroBox : null}
        // The crop is edited HERE now, zoomed — the cover preview is gone. Editable only
        // on the splash's own picture; the other gallery photos just zoom to look at.
        onBoxChange={zoom === heroUrl ? (b) => setSubmodelHeroBox(docId, submodelId, b) : undefined}
        onClose={() => setZoom(null)}
      />
    </div>
  );
}
